<template>
  <div class="c-macro">
    <div class="title">
      {{ $t("keyboard.macro.title") }}
      <span v-if="minInterval < 10 && isCN">
        <br />有线和 2.4G 支持最低 1ms，蓝牙仅支持最低
        10ms，如蓝牙模式下宏设置低于 10ms 将以 10ms 触发宏
      </span>
    </div>
    <div class="list">
      <div
        class="fields"
        v-for="(key, index) in macroKey.layer_key"
        :key="key + index"
      >
        <md-field class="field">
          <md-select
            v-if="activeIndex === index"
            v-model="macroKey.layer_key[index]"
            :disabled="state === 'record'"
            class="key-select"
          >
            <template v-for="(keyItem, key) in keys">
              <md-option v-if="key" :value="key" :key="'key' + key">
                {{ keyItem.nowrapText }}
                {{
                  keyItem.desc ? ` [${keyItem.desc[isCN ? "cn" : "en"]}]` : ""
                }}
              </md-option>
            </template>
          </md-select>
          <md-input
            v-else
            :value="keys[macroKey.layer_key[index]].nowrapText"
            readonly="readonly"
            class="key-input"
            @mouseover="activeIndex = index"
          ></md-input>
          <i
            v-show="state !== 'record'"
            class="key-remove iconfont iconclose"
            @click="onKeyRemove(index)"
          />
        </md-field>
        <i
          v-if="index === macroKey.layer_key.length - 1"
          class="key-add iconfont iconplus-o"
          @click="onKeyAdd"
        />
        <template v-else>
          -
          <md-field class="field time">
            <md-input
              v-model="macroKey.intvel_ms[index]"
              @blur="onIntervalInput(index, $event)"
              :disabled="state === 'record'"
              class="time-input"
            ></md-input>
            <span class="md-suffix">ms</span>
          </md-field>
          —
        </template>
      </div>
    </div>
    <div class="buttons">
      <md-field class="macro-field">
        <md-select
          v-model="macroIndex"
          :disabled="state === 'record'"
          class="macro-select"
          @md-selected="onMacroChange"
          :placeholder="$t('keyboard.macro.select')"
        >
          <template v-for="(macroKey, index) in profile.data.macro_key || []">
            <md-option :value="index" :key="'macro_key' + index">
              M_{{
                parseInt(macroKey.original_key.replace("#009515", ""), 16) + 1
              }}
            </md-option>
          </template>
        </md-select>
      </md-field>
      <a-radio-group class="radio-group" value="none" size="large">
        <a-radio-button :disabled="state === 'record'" @click="onAddMacro">
          {{ $t("keyboard.macro.add") }}
        </a-radio-button>
        <a-radio-button
          :disabled="state === 'record' || macroIndex < 0"
          @click="onRemove"
        >
          {{ $t("keyboard.macro.remove") }}
        </a-radio-button>
        <a-radio-button
          :disabled="macroIndex < 0"
          @click="onRecord"
          v-if="state === 'stop'"
        >
          {{ $t("keyboard.macro.record") }} ●
        </a-radio-button>
        <a-radio-button
          :disabled="macroIndex < 0"
          :class="{ active: state === 'record' }"
          @click="state = 'stop'"
          v-if="state === 'record'"
        >
          {{ $t("keyboard.macro.stop") }} ■
        </a-radio-button>
        <a-radio-button
          :class="{ active: saveActive }"
          :disabled="state === 'record' || macroIndex < 0"
          @click="onSave"
        >
          {{ $t("save") }}
        </a-radio-button>
        <a-radio-button
          :disabled="state === 'record' || macroIndex < 0 || !selectedKey"
          @click="onMacroOk"
        >
          {{ $t("keyboard.macro.replace") }}
        </a-radio-button>
      </a-radio-group>
      <a-progress
        class="progress"
        :stroke-color="{
          '0%': '#4136f1',
          '100%': '#8743ff',
        }"
        :percent="macroLength / 2"
        status="active"
      />
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { macroKeys, codeKeys } from "./SpecialKeys";
import { deepCopy } from "@/utils";

export default {
  name: "CMacro",
  props: {
    dispatchType: String,
    profile: {
      type: Object,
      default: () => {},
    },
    selectedKey: String,
    selectedKeyIndex: Number,
    layerIndex: Number,
    keys: {
      type: Object,
      default: () => {},
    },
    minInterval: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      macroKeys,
      macroIndex: -1,
      macroKey: {
        original_key: "",
        layer_key: [],
        intvel_ms: [],
      },
      macroLength: 0,
      state: "stop",
      time: 0,
      activeIndex: -1,
    };
  },
  computed: {
    ...mapState("app", {
      isCN: "isCN",
    }),
    saveActive() {
      if (this.state === "record") return false;
      if (this.macroIndex < 0) return false;
      if (
        JSON.stringify(this.macroKey) !==
        JSON.stringify(this.profile.data.macro_key[this.macroIndex])
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    onMacroClick() {
      this.disabled = false;
      this.state = "stop";
      let data = this.profile.data;
      if (this.macroKeys.includes(this.selectedKey)) {
        const macroIndex = data.macro_key.findIndex(
          (k) => k.original_key === this.selectedKey
        );
        this.macroKey = deepCopy(data.macro_key[macroIndex]);
        this.macroIndex = macroIndex;
      }
      if (data.macro_key?.length) {
        if (this.macroIndex < 0) {
          this.macroIndex = 0;
          this.macroKey = deepCopy(data.macro_key[0]);
        }
      } else {
        data.macro_key = [];
      }
      this.macroLength = data.macro_key.reduce((acc, item) => {
        return acc + item.layer_key.length;
      }, 0);
    },
    onMacroChange(index) {
      if (this.macroIndex < 0) return;
      this.macroKey = deepCopy(this.profile.data.macro_key[index]);
      this.macroIndex = index;
    },
    onAddMacro() {
      this.state = "stop";
      let data = this.profile.data;
      if (data.macro_key.length >= 32) {
        this.$message.info(this.$t("keyboard.macro.max"));
        return;
      }
      let newKey = "";
      for (let i = 0; i < 32; i++) {
        const key = "#009515" + i.toString(16).toUpperCase().padStart(2, "0");
        const macroKey = data.macro_key.find((k) => k.original_key === key);
        if (!macroKey) {
          newKey = key;
          break;
        }
      }
      this.macroKey = {
        original_key: newKey,
        layer_key: [],
        intvel_ms: [],
      };
      this.macroIndex = data.macro_key.length;
      data.macro_key.push(this.macroKey);
      this.$store.dispatch(this.dispatchType, { ...data });
    },
    onKeyRemove(index) {
      this.macroKey.layer_key.splice(index, 1);
      this.macroKey.intvel_ms.splice(index, 1);
      this.macroKey = { ...this.macroKey };
    },
    onIntervalInput(index, e) {
      let time = e.target.value;
      time = parseInt(time.replace(/[^0-9]/g, "")) || 0;
      if (time < this.minInterval) time = this.minInterval;
      if (time > 15000) time = 15000;
      this.macroKey.intvel_ms[index] = Math.floor(time);
      this.macroKey = { ...this.macroKey };
    },
    onKeyAdd() {
      this.macroKey.layer_key.push("#11070004");
      this.macroKey.intvel_ms.push(this.minInterval);
      this.macroKey = { ...this.macroKey };
    },
    onRemove() {
      this.$confirm({
        title: this.$t("keyboard.macro.removeConfirm"),
        okText: this.$t("yes"),
        cancelText: this.$t("cancel"),
        centered: true,
        onOk: () => {
          let data = deepCopy(this.profile.data);
          let defaultData = this.profile.default;
          data.key_layer.layer_data.map(({ layer }, layerIndex) => {
            layer.map((item, index) => {
              if (item === this.macroKey.original_key)
                layer[index] =
                  defaultData.key_layer.layer_data[layerIndex].layer[index];
            });
          });
          data.macro_key.splice(this.macroIndex, 1);
          this.$store.dispatch(this.dispatchType, data);
          this.$store.dispatch("loader/syncMacroKey", {
            macro_key: data.macro_key,
            original_key: this.macroKey.original_key,
          });
          this.state = "stop";
          this.macroIndex = -1;
          this.macroKey = {
            original_key: "",
            layer_key: [],
            intvel_ms: [],
          };
        },
      });
    },
    onRecord() {
      this.macroKey = {
        ...this.macroKey,
        layer_key: [],
        intvel_ms: [],
      };
      this.state = "record";
    },
    onSave() {
      let macro_key = deepCopy(this.profile.data.macro_key);
      macro_key[this.macroIndex] = deepCopy(this.macroKey);
      const length = macro_key.reduce((acc, item) => {
        return acc + item.layer_key.length;
      }, 0);
      if (length > 200) {
        this.$message.info(
          this.$t("keyboard.macro.maxLength", [200 - this.macroLength])
        );
        return;
      }
      this.macroLength = length;
      this.$store.dispatch(this.dispatchType, {
        ...this.profile.data,
        macro_key,
      });
      this.$store.dispatch("loader/syncMacroKey", {
        macro_key,
        original_key: this.macroKey.original_key,
      });
    },
    onMacroOk() {
      if (this.selectedKey && this.selectedKey !== this.macroKey.original_key) {
        this.$emit("key-click", this.macroKey.original_key);
      }
    },
    onKeyDown(event) {
      if (this.state === "record") {
        const downKey = codeKeys["↓" + event.code];
        if (!downKey) return;
        // const downIndex = this.macroKey.layer_key.lastIndexOf(downKey);
        // const upKey = codeKeys["↑" + event.code];
        // const upIndex = this.macroKey.layer_key.lastIndexOf(upKey);
        // if (downIndex === -1 || upIndex > downIndex) {
        const time = new Date().getTime() - this.time;
        this.macroKey.layer_key.push(downKey);
        if (this.macroKey.layer_key.length - 2 >= 0) {
          this.macroKey.intvel_ms[this.macroKey.layer_key.length - 2] =
            time > this.minInterval ? time : this.minInterval;
        }
        this.time = new Date().getTime();
        // }
      }
    },
    onKeyUp(event) {
      if (this.state === "record") {
        const upKey = codeKeys["↑" + event.code];
        if (!upKey) return;
        const time = new Date().getTime() - this.time;
        this.macroKey.layer_key.push(upKey);
        if (this.macroKey.layer_key.length - 2 >= 0) {
          this.macroKey.intvel_ms[this.macroKey.layer_key.length - 2] =
            time > this.minInterval ? time : this.minInterval;
        }
        this.time = new Date().getTime();
      }
    },
  },
  watch: {
    selectedKey(selectedKey) {
      if (selectedKey) this.onMacroClick();
    },
  },
  mounted() {
    this.onMacroClick();
    document.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("keyup", this.onKeyUp);
    window.syncMacroKeySuccessfully = () => {
      this.$store.dispatch("loader/setLoading", false);
    };
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("keyup", this.onKeyUp);
  },
};
</script>
<style lang="scss" scoped>
.c-macro {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  flex-direction: column;

  .title {
    font-size: 18px;
    color: #ffffff;
    margin-bottom: 30px;
  }

  .list {
    width: 1320px;
    min-height: 155px;
    border: 1px solid $primary-color;
    display: flex;
    align-items: flex-start;
    align-content: flex-start;
    flex-wrap: wrap;
    margin-bottom: 30px;
    padding: 10px;

    .fields {
      @include flex-center;
      font-size: 30px;
      letter-spacing: 0;
      color: $primary-color;

      .field {
        width: 95px;
        margin: 5px 0;
        background: #333333;
        padding: 5px;
        font-size: 14px;
        position: relative;

        .key-remove {
          @include flex-center;
          display: none;
          width: 25px;
          height: 25px;
          border-radius: 25px;
          background: $primary-color;
          color: #ffffff;
          position: absolute;
          right: -8px;
          top: -8px;
          cursor: pointer;
        }

        &:hover .key-remove {
          display: flex;
        }
      }

      .key-add {
        font-size: 30px;
        margin-left: 10px;
        cursor: pointer;
      }

      .time {
        width: 80px;
        font-size: 14px;

        .md-input {
          width: 45px;
        }
      }
    }
  }

  .buttons {
    @include flex-center;
    width: 1300px;

    .macro-select {
      width: 100px;
      height: 42px;
      border: 1px solid $primary-color;
      border-radius: 5px;
      margin-left: 60px;
      padding: 0 5px 0 10px;
      overflow: hidden;
      flex: none;
    }

    .radio-group {
      flex: none;
      margin: 0 40px;

      .active {
        background: $primary-color;
        color: #ffffff;
      }
    }

    .progress {
      width: 200px;
      flex: none;
    }
  }
}
</style>
<style lang="scss">
.c-macro {
  .list .fields .field {
    .key-select {
      .md-input {
        font-size: 1em;
      }

      .md-icon {
        display: none;
      }
    }
    .key-input {
      cursor: pointer;
      max-width: 100%;
      font-size: 1em;
    }
    .time-input {
      font-size: 1em;
    }

    &:hover .key-select {
      .md-icon {
        display: inline-flex;
      }
    }
  }

  .macro-field {
    width: auto;
    height: auto;
    min-height: auto;
    margin: 0;
    padding: 0;

    &::before,
    &::after {
      display: none;
    }
  }

  .progress {
    .ant-progress-inner {
      background-color: #333333;
    }
    .ant-progress-text {
      color: $primary-color;
    }
  }
}
</style>
